/**
 * Threshold + separable blur + additive recombine bloom.
 *
 * The whole point of this filter is what it does *not* light up. A naive
 * full-screen glow turns the plum-black felt into grey soup; this one runs a
 * soft-knee bright pass first, so the felt (0x1B1030, brightness ~0.15) and the
 * rail (0x2A1A4A, ~0.23) sit far below the threshold and contribute nothing,
 * while gold (~0.91), mint (~0.74) and the wild's foil highlights (~1.0) push
 * through. Saturated-but-dark colours would be missed by a pure luma test, so
 * brightness is a blend of luma and max-channel — that is what lets violet
 * register at all without dragging the felt up with it.
 *
 * Passes, all inside one `apply()`:
 *   1. bright pass, 2x2 tent downsample to half resolution
 *   2. N iterations of a 5-tap linear-sampled Gaussian, horizontal + vertical,
 *      each iteration doubling the radius — cheap, and much smoother than one
 *      wide pass
 *   3. additive recombine against the untouched input
 *
 * The intermediate targets are logical-size-matched to the input but allocated
 * at half its resolution, which keeps `uOutputFrame`/`uOutputTexture` in
 * agreement (Pixi sizes the filter quad from the *input frame*) while doing a
 * quarter of the pixel work.
 */

import {
  CanvasSource,
  Filter,
  GlProgram,
  Texture,
  TexturePool,
  defaultFilterVert,
} from 'pixi.js';
import type { FilterSystem, RenderSurface } from 'pixi.js';

import { LIGHT } from '@/pixi/layout';

import type { CardShield, ShieldUniforms } from './CardShield';
import { GLSL_SHIELD, shieldResources } from './CardShield';
import { GLSL_COLOUR, GLSL_FRAME, GLSL_GLOBALS, mixRgb, rgb01, vec3f } from './glsl';

/** Below this the bloom is a no-op and the sub-passes are skipped entirely. */
const OFF_EPSILON = 0.002;

/** Edge of the baked lens-dirt texture, in pixels. Low frequency, so small. */
const DIRT_SIZE = 512;

/**
 * Bake a lens-dirt mask: soft elongated smudges and a few arcs.
 *
 * Real glass is never clean, and a perfectly isotropic radial glow is the
 * clearest tell that a bloom was added in post rather than photographed. This
 * modulates the *glow* and not the base image, so it is completely invisible
 * until something on the table actually flares — which is exactly the
 * behaviour wanted, and why it costs nothing at rest.
 *
 * Deliberately low frequency and centred on a mean near 0.5, so the composite's
 * `1 + amount * (dirt * 2 - 1)` averages out to unity and the overall bloom
 * strength is unchanged — only its evenness.
 */
function bakeDirt(): CanvasSource {
  const canvas = document.createElement('canvas');
  canvas.width = DIRT_SIZE;
  canvas.height = DIRT_SIZE;

  const source = new CanvasSource({
    resource: canvas,
    resolution: 1,
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
    label: 'bloom-lens-dirt',
  });

  const ctx = canvas.getContext('2d');
  if (!ctx) return source;

  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, DIRT_SIZE, DIRT_SIZE);

  // Deterministic: the same lens every session. A dirt pattern that changed on
  // reload would read as flicker rather than as glass.
  let seed = 0x2f6e2b1;
  const rnd = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };

  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 42; i++) {
    const x = rnd() * DIRT_SIZE;
    const y = rnd() * DIRT_SIZE;
    const len = 12 + rnd() * 90;
    const thick = 4 + rnd() * 22;
    const angle = rnd() * Math.PI;
    const strength = 0.05 + rnd() * 0.16;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.scale(len / thick, 1);
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, thick);
    grad.addColorStop(0, `rgba(255, 255, 255, ${strength})`);
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, thick, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // A few wipe arcs — the signature of a cloth having been dragged across.
  ctx.globalCompositeOperation = 'source-over';
  for (let i = 0; i < 5; i++) {
    ctx.beginPath();
    ctx.arc(
      rnd() * DIRT_SIZE,
      rnd() * DIRT_SIZE,
      60 + rnd() * 160,
      rnd() * Math.PI * 2,
      rnd() * Math.PI * 2 + 1.2,
    );
    ctx.strokeStyle = `rgba(0, 0, 0, ${0.04 + rnd() * 0.06})`;
    ctx.lineWidth = 6 + rnd() * 18;
    ctx.stroke();
  }

  source.update();
  return source;
}

let dirtSource: CanvasSource | null = null;

/** One lens for the whole app. Baked on first use. */
function getDirt(): CanvasSource {
  dirtSource ??= bakeDirt();
  return dirtSource;
}

/** Gaussian sampled at the linear-filtered midpoints: 9 taps for 5 fetches. */
const TAP_OFFSET_1 = 1.3846153846;
const TAP_OFFSET_2 = 3.2307692308;
const TAP_WEIGHT_0 = 0.2270270270;
const TAP_WEIGHT_1 = 0.3162162162;
const TAP_WEIGHT_2 = 0.0702702703;

const brightFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
${GLSL_GLOBALS}
uniform float uThreshold;
uniform float uKnee;
uniform float uSaturationBias;
uniform float uSampleSpread;

${GLSL_COLOUR}

vec4 brightPass(vec2 tc) {
    vec4 src = texture(uTexture, clamp(tc, uInputClamp.xy, uInputClamp.zw));
    vec3 rgb = unpremultiply(src);

    // Decode to pseudo-linear (gamma 2.0) BEFORE thresholding, and emit linear.
    //
    // Convolution is a linear operation on radiance. Blurring gamma-encoded
    // values over-weights dim pixels — a 50/50 average of a hot pixel and a
    // dark one gives display 0.5, which is linear 0.22, where the physically
    // correct answer is linear 0.5 (display 0.71). The visible result is a halo
    // that is too bright far out and too dim at the core: a milky wash instead
    // of a hot centre with a long faint skirt. That is the single physical
    // difference between cheap bloom and expensive bloom.
    //
    // The whole downsample/blur/composite path downstream is therefore in
    // linear light, and the composite re-encodes once at the end.
    vec3 lin = rgb * rgb;

    // Luma alone misses saturated darks (violet), max-channel alone drags the
    // felt up. Blending the two is what makes the threshold behave.
    float brightness = mix(luma(lin), maxChannel(lin), uSaturationBias);

    float knee = max(uThreshold * uKnee, 1e-4);
    float soft = clamp(brightness - uThreshold + knee, 0.0, 2.0 * knee);
    soft = soft * soft / (4.0 * knee);

    float contribution = max(soft, brightness - uThreshold) / max(brightness, 1e-4);
    return vec4(lin * contribution * src.a, src.a);
}

void main(void) {
    // 2x2 tent while downsampling, so a one-pixel specular does not strobe.
    vec2 o = uInputPixel.zw * uSampleSpread * 0.5;
    vec4 acc = brightPass(vTextureCoord + vec2(-o.x, -o.y));
    acc += brightPass(vTextureCoord + vec2(o.x, -o.y));
    acc += brightPass(vTextureCoord + vec2(-o.x, o.y));
    acc += brightPass(vTextureCoord + vec2(o.x, o.y));
    finalColor = acc * 0.25;
}
`;

const blurFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
${GLSL_GLOBALS}
uniform vec2 uStep;

void main(void) {
    vec2 d = uStep * uInputPixel.zw;

    vec4 acc = texture(uTexture, vTextureCoord) * ${TAP_WEIGHT_0.toFixed(10)};
    acc += texture(uTexture, clamp(vTextureCoord + d * ${TAP_OFFSET_1.toFixed(10)}, uInputClamp.xy, uInputClamp.zw)) * ${TAP_WEIGHT_1.toFixed(10)};
    acc += texture(uTexture, clamp(vTextureCoord - d * ${TAP_OFFSET_1.toFixed(10)}, uInputClamp.xy, uInputClamp.zw)) * ${TAP_WEIGHT_1.toFixed(10)};
    acc += texture(uTexture, clamp(vTextureCoord + d * ${TAP_OFFSET_2.toFixed(10)}, uInputClamp.xy, uInputClamp.zw)) * ${TAP_WEIGHT_2.toFixed(10)};
    acc += texture(uTexture, clamp(vTextureCoord - d * ${TAP_OFFSET_2.toFixed(10)}, uInputClamp.xy, uInputClamp.zw)) * ${TAP_WEIGHT_2.toFixed(10)};

    finalColor = acc;
}
`;

const compositeFragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uBloomTexture;
uniform sampler2D uDirtTexture;
${GLSL_GLOBALS}
uniform vec2 uBloomScale;
uniform vec4 uBloomClamp;
uniform float uIntensity;
uniform float uShoulder;
uniform float uDirtAmount;
uniform vec3 uTint;

${GLSL_COLOUR}
${GLSL_FRAME}
${GLSL_SHIELD}

/**
 * Soft highlight rolloff: identity below the knee, asymptotic to 1 above it.
 *
 * Replaces a hard min(x, 1.0) clamp. At full boost a gold flare summed to
 * (1.44, 1.11, 0.34) in display space, so red and green both slammed into the
 * ceiling while blue was untouched — the brand gold turned into a flat lemon
 * yellow at exactly the moment the player was looking hardest at it, with no
 * gradation inside the core. It also meant nothing above 1.0 ever reached the
 * colour grade, so the grade's filmic shoulder was guarding a door the bloom
 * had already welded shut.
 *
 * Below the knee this is bit-identical to no tonemap at all, so only the top of
 * the range pays anything.
 */
vec3 softShoulder(vec3 x, float knee) {
    vec3 over = max(x - knee, 0.0);
    float range = max(1.0 - knee, 1e-4);
    return min(x, vec3(knee)) + range * (over / (over + range));
}

void main(void) {
    vec4 base = texture(uTexture, vTextureCoord);

    // The bloom target may be a different physical size to the input, so map
    // through its own UV scale rather than assuming they share a source.
    vec2 buv = clamp(vTextureCoord * uBloomScale, uBloomClamp.xy, uBloomClamp.zw);

    // Lens dirt modulates the GLOW, never the base image, so it is invisible
    // until something flares and then makes the flare bloom unevenly the way it
    // would through real glass. Mean 0.5 in the texture, so this averages to
    // unity and changes the evenness of the bloom rather than its strength.
    float dirt = texture(uDirtTexture, frameUv(vTextureCoord)).r;
    float dirtGain = 1.0 + uDirtAmount * (dirt * 2.0 - 1.0);

    vec3 glow = texture(uBloomTexture, buv).rgb * uTint * uIntensity * dirtGain;

    // The glow arrives linear (see brightPass), so the base has to be decoded
    // to meet it, summed there, rolled off, and encoded once on the way out.
    vec3 baseLin = unpremultiply(base);
    baseLin *= baseLin;

    vec3 mapped = softShoulder(baseLin + glow, uShoulder);
    vec3 encoded = sqrt(max(mapped, 0.0));

    // Additive, but kept inside the premultiplied envelope so the result is
    // still a legal texture for whatever samples it next.
    float a = clamp(base.a + luma(glow), 0.0, 1.0);

    // A card is never part of the lens: where one is, the pixel this pass was
    // given is the pixel it returns. See CardShield.
    finalColor = mix(vec4(encoded * a, a), base, shielded(vTextureCoord));
}
`;

export interface BloomFilterOptions {
  /** Brightness above which a pixel starts to bloom. 0..1. */
  threshold?: number;
  /** Soft-knee width as a fraction of the threshold. 0 = hard cut. */
  knee?: number;
  /**
   * Linear value at which the highlight rolloff starts, 0..1.
   *
   * Below this the composite is untouched; above it, base+glow is compressed
   * asymptotically toward 1 instead of being hard-clipped. Lower is a softer,
   * more filmic top end; 1 restores the old clipping behaviour.
   */
  shoulder?: number;
  /**
   * Strength of the lens-dirt modulation on the glow, 0..1.
   *
   * 0 is a perfectly clean, perfectly isotropic halo — which is the single
   * clearest sign a bloom was added in post. Only visible while something is
   * flaring.
   */
  dirtAmount?: number;
  /** 0 = pure luma test, 1 = pure max-channel. Lets saturated darks register. */
  saturationBias?: number;
  /** Additive strength of the recombine. */
  intensity?: number;
  /** Base blur radius in bloom-target pixels. Doubles each iteration. */
  spread?: number;
  /** Blur iterations. More = wider and smoother, at a quarter-res cost each. */
  iterations?: number;
  /** Bloom target resolution divisor. 2 = half resolution. */
  downsample?: number;
  /** Glow tint. Defaults to the key light, mostly neutralised. */
  tint?: number;
}

const DEFAULTS: Required<Omit<BloomFilterOptions, 'tint'>> = {
  // Retuned for the linear-light bright pass. Measured with the shader's own
  // mix(luma, maxChannel, 0.6) metric AFTER the gamma-2.0 decode:
  //   felt 0x1B1030 -> 0.024   rail 0x2A1A4A -> 0.058   (both contribute zero)
  //   violet -> 0.528 (knee only)   mint -> 0.621   violetLight -> 0.748
  //   gold 0xFFC53D -> 0.857 (contributes most)
  // Same ranking as the old display-space 0.66/0.55, different absolute scale.
  threshold: 0.42,
  knee: 0.45,
  shoulder: 0.82,
  dirtAmount: 0.35,
  saturationBias: 0.6,
  intensity: 0.45,
  spread: 2.6,
  iterations: 3,
  downsample: 2,
};

/** Warm but nearly neutral, so a gold flare stays gold and mint stays mint. */
const DEFAULT_TINT = mixRgb([1, 1, 1], rgb01(LIGHT.colour), 0.35);

interface BrightUniforms {
  uThreshold: number;
  uKnee: number;
  uSaturationBias: number;
  uSampleSpread: number;
}

interface BlurUniforms {
  uStep: Float32Array;
}

interface CompositeUniforms {
  uBloomScale: Float32Array;
  uBloomClamp: Float32Array;
  uIntensity: number;
  uShoulder: number;
  uDirtAmount: number;
  uTint: Float32Array;
}

export class BloomFilter extends Filter {
  /** Soft-knee bright pass, also responsible for the downsample. */
  readonly #bright: Filter;
  /** Single separable Gaussian, re-used for both axes. */
  readonly #blur: Filter;

  readonly #brightUniforms: BrightUniforms;
  readonly #blurUniforms: BlurUniforms;
  readonly #uniforms: CompositeUniforms;
  readonly #shieldUniforms: ShieldUniforms;

  /** Where the cards are, if they are to be left out of the composite. See CardShield. */
  shield: CardShield | null = null;

  #spread: number;
  #iterations: number;
  #downsample: number;

  constructor(options: BloomFilterOptions = {}) {
    const o = { ...DEFAULTS, ...options };

    super({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment: compositeFragment,
        name: 'wild21-bloom-composite',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        uBloomTexture: Texture.EMPTY.source,
        uDirtTexture: getDirt(),
        ...shieldResources(),
        bloomUniforms: {
          uBloomScale: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
          uBloomClamp: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
          uIntensity: { value: o.intensity, type: 'f32' },
          uShoulder: { value: o.shoulder, type: 'f32' },
          uDirtAmount: { value: o.dirtAmount, type: 'f32' },
          uTint: {
            value: vec3f(options.tint === undefined ? DEFAULT_TINT : rgb01(options.tint)),
            type: 'vec3<f32>',
          },
        },
      },
      // Filter textures inherit the renderer's resolution and antialias so the
      // scene does not lose retina sharpness or MSAA on its way through post.
      resolution: 'inherit',
      antialias: 'inherit',
      padding: 0,
    });

    this.#uniforms = this.resources.bloomUniforms.uniforms as CompositeUniforms;
    this.#shieldUniforms = this.resources.shieldUniforms.uniforms as ShieldUniforms;

    this.#bright = new Filter({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment: brightFragment,
        name: 'wild21-bloom-bright',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        brightUniforms: {
          uThreshold: { value: o.threshold, type: 'f32' },
          uKnee: { value: o.knee, type: 'f32' },
          uSaturationBias: { value: o.saturationBias, type: 'f32' },
          uSampleSpread: { value: o.downsample, type: 'f32' },
        },
      },
      resolution: 'inherit',
      antialias: 'off',
    });
    this.#brightUniforms = this.#bright.resources.brightUniforms.uniforms as BrightUniforms;

    this.#blur = new Filter({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment: blurFragment,
        name: 'wild21-bloom-blur',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        blurUniforms: {
          uStep: { value: new Float32Array([1, 0]), type: 'vec2<f32>' },
        },
      },
      resolution: 'inherit',
      antialias: 'off',
    });
    this.#blurUniforms = this.#blur.resources.blurUniforms.uniforms as BlurUniforms;

    this.#spread = o.spread;
    this.#iterations = Math.max(1, Math.round(o.iterations));
    this.#downsample = Math.max(1, o.downsample);
  }

  /** Additive strength of the glow. vfx pushes this up on a big win. */
  get intensity(): number {
    return this.#uniforms.uIntensity;
  }
  set intensity(value: number) {
    this.#uniforms.uIntensity = Math.max(0, value);
  }

  /** Brightness a pixel must exceed before it blooms at all. */
  get threshold(): number {
    return this.#brightUniforms.uThreshold;
  }
  set threshold(value: number) {
    this.#brightUniforms.uThreshold = value;
  }

  /** Soft-knee width, as a fraction of the threshold. */
  get knee(): number {
    return this.#brightUniforms.uKnee;
  }
  set knee(value: number) {
    this.#brightUniforms.uKnee = value;
  }

  /** 0 = luma only, 1 = max-channel only. */
  get saturationBias(): number {
    return this.#brightUniforms.uSaturationBias;
  }
  set saturationBias(value: number) {
    this.#brightUniforms.uSaturationBias = value;
  }

  /** Base blur radius, in bloom-target pixels. */
  get spread(): number {
    return this.#spread;
  }
  set spread(value: number) {
    this.#spread = Math.max(0, value);
  }

  /** Number of doubling blur iterations. */
  get iterations(): number {
    return this.#iterations;
  }
  set iterations(value: number) {
    this.#iterations = Math.max(1, Math.round(value));
  }

  /** Resolution divisor for the bloom targets. 1 = full res, 2 = half. */
  get downsample(): number {
    return this.#downsample;
  }
  set downsample(value: number) {
    this.#downsample = Math.max(1, value);
    this.#brightUniforms.uSampleSpread = this.#downsample;
  }

  override apply(
    filterManager: FilterSystem,
    input: Texture,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    if (this.#uniforms.uIntensity <= OFF_EPSILON) {
      // Nothing is flaring: skip the bright pass and the blur entirely and let
      // the composite run as a straight copy.
      this.resources.uBloomTexture = Texture.EMPTY.source;
      this.#bindShield(input);
      filterManager.applyFilter(this, input, output, clearMode);
      return;
    }

    const frameWidth = input.frame.width;
    const frameHeight = input.frame.height;
    const targetResolution = input.source.resolution / this.#downsample;

    // Same logical frame, fewer pixels: the filter quad maths stays exact.
    let front = TexturePool.getOptimalTexture(frameWidth, frameHeight, targetResolution, false);
    let back = TexturePool.getOptimalTexture(frameWidth, frameHeight, targetResolution, false);

    filterManager.applyFilter(this.#bright, input, front, true);

    const step = this.#blurUniforms.uStep;
    for (let i = 0; i < this.#iterations; i++) {
      const radius = this.#spread * 2 ** i;

      step[0] = radius;
      step[1] = 0;
      filterManager.applyFilter(this.#blur, front, back, true);
      [front, back] = [back, front];

      step[0] = 0;
      step[1] = radius;
      filterManager.applyFilter(this.#blur, front, back, true);
      [front, back] = [back, front];
    }

    const bloomSource = front.source;
    const scale = this.#uniforms.uBloomScale;
    scale[0] = input.source.width / bloomSource.width;
    scale[1] = input.source.height / bloomSource.height;

    const halfTexelX = 0.5 / bloomSource.pixelWidth;
    const halfTexelY = 0.5 / bloomSource.pixelHeight;
    const clampUv = this.#uniforms.uBloomClamp;
    clampUv[0] = halfTexelX;
    clampUv[1] = halfTexelY;
    clampUv[2] = front.frame.width / bloomSource.width - halfTexelX;
    clampUv[3] = front.frame.height / bloomSource.height - halfTexelY;

    this.resources.uBloomTexture = bloomSource;
    this.#bindShield(input);
    filterManager.applyFilter(this, input, output, clearMode);

    this.resources.uBloomTexture = Texture.EMPTY.source;
    TexturePool.returnTexture(front);
    TexturePool.returnTexture(back);
  }

  #bindShield(input: Texture): void {
    if (this.shield) this.shield.bind(this, this.#shieldUniforms, input);
    else this.#shieldUniforms.uShield = 0;
  }

  override destroy(): void {
    this.#bright.destroy();
    this.#blur.destroy();
    super.destroy();
  }
}
